import { Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';

interface FilterDropdownProps {
  open: boolean;
  /** The button the dropdown anchors to; the caret points at its center. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
  /** Panel width in px (default 460). */
  width?: number;
}

const VIEWPORT_MARGIN = 8;
const CARET_HALF = 12;
/** Gap kept between the panel's bottom edge and the bottom of the viewport. */
const VIEWPORT_BOTTOM_GAP = 10;
const PANEL_BG = '#0A0E18';
const PANEL_BORDER = '#1E2638';

const FILTER_DROPDOWN_Z_INDEX = 1500;

/**
 * Anchored popover panel for the repertoire filter. Measures the trigger button
 * and positions itself directly beneath it, clamped to the viewport so it never
 * runs off-screen. A caret at the top tracks the button center even after
 * clamping shifts the panel sideways. The body scrolls when it would otherwise
 * extend past the viewport bottom, with a down-caret hint (as in Tempo) when
 * there's more to see. Closes on outside pointer-down.
 */
export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  open,
  anchorRef,
  onClose,
  children,
  width = 460,
}) => {
  // Android's Back closes the dropdown as a tap outside it does.
  useHardwareBackClosesLayer(open, onClose, FILTER_DROPDOWN_Z_INDEX);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{
    left: number;
    top: number;
    caretLeft: number;
    maxHeight: number;
  } | null>(null);
  const [showScrollMoreHint, setShowScrollMoreHint] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const btn = anchorRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const vw = window.innerWidth;
      const center = rect.left + rect.width / 2;
      let left = center - width / 2;
      left = Math.min(left, vw - width - VIEWPORT_MARGIN);
      left = Math.max(left, VIEWPORT_MARGIN);
      const caretLeft = Math.min(
        Math.max(center - left - CARET_HALF, CARET_HALF),
        width - 3 * CARET_HALF,
      );
      const top = rect.bottom + 12;
      const maxHeight = window.innerHeight - top - VIEWPORT_BOTTOM_GAP;
      setAnchor({ left, top, caretLeft, maxHeight });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, anchorRef, width]);

  // Track whether the body overflows and isn't scrolled to the bottom, to show
  // the down-caret "there's more" hint.
  const updateScrollState = useCallback(() => {
    const el = scrollBodyRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    const epsilon = 2;
    const overflow = scrollHeight > clientHeight + epsilon;
    const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
    setShowScrollMoreHint(overflow && !atBottom);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const el = scrollBodyRef.current;
    const inner = scrollContentRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    if (inner) ro.observe(inner);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [open, updateScrollState, anchor?.maxHeight]);

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      // MUI Select / Menu render their dropdown in a portal outside this panel.
      // Clicks on those options must not close the filter.
      const el = target instanceof Element ? target : null;
      if (el?.closest('.MuiPopover-root, .MuiModal-root, .MuiMenu-root')) {
        return;
      }
      onClose();
    },
    [anchorRef, onClose],
  );

  useEffect(() => {
    if (!open) return;
    // pointerdown (not mousedown) so taps on the score canvas / iOS Safari close it.
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open, handlePointerDown]);

  if (!open) return null;

  return (
    <Box
      ref={panelRef}
      sx={{
        position: 'fixed',
        left: `${anchor?.left ?? VIEWPORT_MARGIN}px`,
        top: `${anchor?.top ?? 84}px`,
        width: `${width}px`,
        maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
        zIndex: FILTER_DROPDOWN_Z_INDEX,
        backgroundColor: PANEL_BG,
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: '12px',
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        animation: 'filterFadeInSlideDown 0.2s ease-out',
        '@keyframes filterFadeInSlideDown': {
          '0%': { opacity: 0, transform: 'translateY(-10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Caret pointing up at the trigger button */}
      <Box
        sx={{
          position: 'absolute',
          top: `-${CARET_HALF}px`,
          left: `${anchor?.caretLeft ?? CARET_HALF}px`,
          width: 0,
          height: 0,
          borderLeft: `${CARET_HALF}px solid transparent`,
          borderRight: `${CARET_HALF}px solid transparent`,
          borderBottom: `${CARET_HALF}px solid ${PANEL_BG}`,
        }}
      />
      {/* Caret border sliver so the outline reads continuously */}
      <Box
        sx={{
          position: 'absolute',
          top: `-${CARET_HALF + 1}px`,
          left: `${anchor?.caretLeft ?? CARET_HALF}px`,
          width: 0,
          height: 0,
          borderLeft: `${CARET_HALF}px solid transparent`,
          borderRight: `${CARET_HALF}px solid transparent`,
          borderBottom: `${CARET_HALF}px solid ${PANEL_BORDER}`,
          zIndex: -1,
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <Box
          ref={scrollBodyRef}
          sx={{
            maxHeight: `${anchor?.maxHeight ?? 0}px`,
            overflowY: 'scroll',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box ref={scrollContentRef}>{children}</Box>
        </Box>
        {showScrollMoreHint && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: '5px',
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              lineHeight: 0,
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 22, color: 'rgba(255, 255, 255, 0.52)' }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FilterDropdown;
